import type {
  DeliveryScheduleCoverage,
  DeliveryScheduleCustomRow,
  DeliveryScheduleRow,
  ListResponse,
  PortalAssortmentRow,
  PortalDeliveryDatesResponse,
  PortalRestaurant,
  RestaurantDeliveryConfig,
  SalesOrderBulkStatusResult,
  SalesOrderDetail,
  SalesOrderGroupRow,
  SalesOrderHistoryEntry,
  SalesOrderListRow,
  SalesOrderStatus,
  StaffOrderStats,
  StaffOrderStatus,
} from '@warehouse/shared';

import { api } from '../../lib/api-client';
import { listQueryParams, type ListState } from '../../lib/list-query';
import { commandId } from '../../lib/command-id';

/**
 * The module's HTTP surface, in one file, so a page never builds a URL itself.
 * Mutations pass a client-generated `commandId` where the endpoint accepts one:
 * a double-tapped "Skicka beställning" on a flaky restaurant wifi must produce
 * ONE order (ARCHITECTURE §4 idempotency).
 */

export const ORDER_QUERY_KEYS = {
  list: (state: ListState) => ['sales-orders', 'list', listQueryParams(state)] as const,
  groups: (state: ListState) => ['sales-orders', 'groups', listQueryParams(state)] as const,
  detail: (id: string) => ['sales-orders', 'detail', id] as const,
  history: (id: string) => ['sales-orders', 'history', id] as const,
  staffList: (state: ListState) => ['staff-orders', 'list', listQueryParams(state)] as const,
  staffStats: () => ['staff-orders', 'stats'] as const,
  schedules: (state: ListState) => ['delivery-schedules', 'list', listQueryParams(state)] as const,
  scheduleCoverage: () => ['delivery-schedules', 'coverage'] as const,
  customDates: (restaurantId?: string) => ['delivery-schedules', 'custom', restaurantId ?? 'all'] as const,
  restaurantDelivery: (restaurantId: string) => ['delivery-schedules', 'restaurant', restaurantId] as const,
  restaurants: () => ['portal', 'restaurants'] as const,
  assortment: (state: ListState) => ['portal', 'assortment', listQueryParams(state)] as const,
  orderArticles: (state: ListState) => ['orders', 'articles', listQueryParams(state)] as const,
  categories: () => ['portal', 'categories'] as const,
  allCategories: () => ['orders', 'categories'] as const,
  deliveryDates: (restaurantId: string, categoryIds: string[]) =>
    ['portal', 'delivery-dates', restaurantId, [...categoryIds].sort().join(',')] as const,
} as const;

const newCommandId = (): string => commandId();

// ---------------------------------------------------------------------------
// sales orders
// ---------------------------------------------------------------------------

export interface OrderLineInput {
  articleId: string;
  qty: string;
  unitId?: string;
}

export const salesOrdersApi = {
  list: (state: ListState) =>
    api.get<ListResponse<SalesOrderListRow>>('/orders/sales', { query: listQueryParams(state) }),

  groups: (state: ListState) =>
    api.get<{ items: SalesOrderGroupRow[] }>('/orders/sales/groups', { query: listQueryParams(state) }),

  detail: (id: string) => api.get<SalesOrderDetail>(`/orders/sales/${id}`),

  history: (id: string) => api.get<{ items: SalesOrderHistoryEntry[] }>(`/orders/sales/${id}/history`),

  create: (input: { restaurantId: string; deliveryDate: string; note?: string; lines: OrderLineInput[] }) =>
    api.post<SalesOrderDetail>('/orders/sales', { commandId: newCommandId(), ...input }),

  update: (
    id: string,
    input: { deliveryDate?: string; note?: string | null; lines?: OrderLineInput[] },
  ) => api.patch<SalesOrderDetail>(`/orders/sales/${id}`, { commandId: newCommandId(), ...input }),

  setStatus: (id: string, status: Exclude<SalesOrderStatus, 'cancelled'>) =>
    api.post<SalesOrderDetail>(`/orders/sales/${id}/status`, { commandId: newCommandId(), status }),

  bulkStatus: (input: {
    status: Exclude<SalesOrderStatus, 'cancelled'>;
    orderIds?: string[];
    restaurantId?: string;
    deliveryDate?: string;
  }) => api.post<SalesOrderBulkStatusResult>('/orders/sales/status', { commandId: newCommandId(), ...input }),

  cancel: (id: string, reason?: string) =>
    api.post<SalesOrderDetail>(`/orders/sales/${id}/cancel`, { commandId: newCommandId(), reason }),

  /**
   * CROSS-MODULE placeholder: M1 (master-data) owns the article register. Until
   * its search endpoint exists, order entry uses the module's own lookup.
   */
  articles: (state: ListState) =>
    api.get<ListResponse<PortalAssortmentRow>>('/orders/articles', { query: listQueryParams(state) }),

  /** Every article category — the delivery-schedule override picker. */
  categories: () => api.get<{ items: { id: string; name: string; sort: number }[] }>('/orders/categories'),
};

// ---------------------------------------------------------------------------
// staff orders
// ---------------------------------------------------------------------------

export const staffOrdersApi = {
  list: (state: ListState) =>
    api.get<ListResponse<SalesOrderListRow>>('/orders/staff', { query: listQueryParams(state) }),

  stats: () => api.get<StaffOrderStats>('/orders/staff/stats'),

  detail: (id: string) => api.get<SalesOrderDetail>(`/orders/staff/${id}`),

  history: (id: string) => api.get<{ items: SalesOrderHistoryEntry[] }>(`/orders/staff/${id}/history`),

  create: (input: {
    restaurantId: string;
    collectedBy?: string;
    note?: string;
    lines: OrderLineInput[];
  }) => api.post<SalesOrderDetail>('/orders/staff', { commandId: newCommandId(), ...input }),

  setStatus: (id: string, status: StaffOrderStatus, collectedBy?: string) =>
    api.post<SalesOrderDetail>(`/orders/staff/${id}/status`, {
      commandId: newCommandId(),
      status,
      collectedBy,
    }),
};

// ---------------------------------------------------------------------------
// delivery schedule
// ---------------------------------------------------------------------------

export interface DeliveryScheduleFormValues {
  restaurantId: string;
  categoryId: string | null;
  deliveryDay1: number;
  dueDay1: number;
  dueTime1: string | null;
  deliveryDay2: number;
  dueDay2: number;
  dueTime2: string | null;
}

export const deliveryScheduleApi = {
  list: (state: ListState) =>
    api.get<ListResponse<DeliveryScheduleRow>>('/orders/delivery-schedules', {
      query: listQueryParams(state),
    }),

  coverage: () => api.get<{ items: DeliveryScheduleCoverage[] }>('/orders/delivery-schedules/coverage'),

  customDates: (restaurantId?: string) =>
    api.get<{ items: DeliveryScheduleCustomRow[] }>('/orders/delivery-schedules/custom', {
      query: restaurantId ? { restaurantId } : undefined,
    }),

  forRestaurant: (restaurantId: string) =>
    api.get<RestaurantDeliveryConfig>(`/orders/delivery/${restaurantId}`),

  create: (input: DeliveryScheduleFormValues) =>
    api.post<DeliveryScheduleRow>('/orders/delivery-schedules', { commandId: newCommandId(), ...input }),

  update: (id: string, input: Omit<DeliveryScheduleFormValues, 'restaurantId' | 'categoryId'>) =>
    api.patch<DeliveryScheduleRow>(`/orders/delivery-schedules/${id}`, {
      commandId: newCommandId(),
      ...input,
    }),

  remove: (id: string) =>
    api.delete<{ id: string }>(`/orders/delivery-schedules/${id}`, {
      query: { commandId: newCommandId() },
    }),

  createCustomDate: (input: {
    restaurantId: string;
    changedDate: string;
    newDate: string;
    note?: string | null;
  }) =>
    api.post<DeliveryScheduleCustomRow>('/orders/delivery-schedules/custom', {
      commandId: newCommandId(),
      ...input,
    }),

  removeCustomDate: (id: string) =>
    api.delete<{ id: string }>(`/orders/delivery-schedules/custom/${id}`, {
      query: { commandId: newCommandId() },
    }),
};

// ---------------------------------------------------------------------------
// portal
// ---------------------------------------------------------------------------

export const portalApi = {
  restaurants: () => api.get<{ items: PortalRestaurant[] }>('/portal/restaurants'),

  assortment: (state: ListState) =>
    api.get<ListResponse<PortalAssortmentRow>>('/portal/assortment', { query: listQueryParams(state) }),

  categories: () =>
    api.get<{ items: { id: string; name: string; sort: number; articleCount: number }[] }>(
      '/portal/categories',
    ),

  deliveryDates: (restaurantId: string, categoryIds: string[]) =>
    api.get<PortalDeliveryDatesResponse>('/portal/delivery-dates', {
      query: { restaurantId, ...(categoryIds.length > 0 ? { categoryIds: categoryIds.join(',') } : {}) },
    }),

  createOrder: (input: {
    restaurantId: string;
    deliveryDate: string;
    note?: string;
    lines: OrderLineInput[];
  }) => api.post<SalesOrderDetail>('/portal/orders', { commandId: newCommandId(), ...input }),

  orders: (state: ListState) =>
    api.get<ListResponse<SalesOrderListRow>>('/portal/orders', { query: listQueryParams(state) }),

  order: (id: string) => api.get<SalesOrderDetail>(`/portal/orders/${id}`),
};
